/**
 * The change feed and what it carries. The server keeps one log of every row the account owns
 * that has moved, and a client pulls from wherever it last got to — so a screen's list is
 * worked out from what this device holds rather than asked for a route at a time.
 */

import type { DailyMetric } from '~/data/metrics'
import type { Entry, Food, Goal, Meal, MealLog } from '~/data/nutrition'
import type { Enrollment, PlanEntry, TemplateVersion, WorkoutTemplate } from '~/data/templates'
import type { AccountSettings, DistanceUnits, LoggedSet, Units, Workout } from '~/data/types'
import type { WeighIn } from '~/data/weighins'

/** A day of the diary as the feed carries it: whether it was said to be a partial record. */
export interface NutritionDay {
  readonly day: string
  readonly incomplete: boolean
}

/** A food marked, under the key the food is known by. */
export interface FavouriteRow {
  readonly key: string
  readonly food: Food
  readonly createdAt: number
}

/** A meal marked, as the copy taken when it was marked. */
export interface MealFavouriteRow {
  readonly mealId: string
  readonly meal: Meal
  readonly createdAt: number
}

/**
 * The account's own settings as one row. Everything is optional: a row written by a server
 * that has learned a new setting reaches a build that has not, and the fields it does carry
 * are still the account's.
 */
export interface SettingsRow extends Partial<AccountSettings> {
  readonly units?: Units | null
  readonly distanceUnits?: DistanceUnits | null
  readonly weeklyCheckinOn?: string | null
}

/**
 * A workout as the wire carries it. The server has always called the sets a workout planned
 * `plannedSets` when it hands one over, and taken them as `plan` when it is given one; every
 * app maps the one to the other as a row lands, rather than carrying two names for the same
 * thing through its own screens.
 */
export type WorkoutRow = Omit<Workout, 'plan'> & { readonly plannedSets?: PlanEntry[] }

/** A workout row as it is held: what it played under the name the rest of this app uses. */
export function workoutFromWire(row: WorkoutRow): Workout {
  const { plannedSets, ...rest } = row
  return { ...rest, plan: plannedSets ?? [] }
}

/** Every kind of row the feed carries, and what one of that kind looks like. */
export interface SyncRows {
  set: LoggedSet
  workout: Workout
  template: WorkoutTemplate
  version: TemplateVersion
  enrollment: Enrollment
  weighin: WeighIn
  metric: DailyMetric
  food: Food
  entry: Entry
  day: NutritionDay
  goal: Goal
  favourite: FavouriteRow
  meal: Meal
  mealFavourite: MealFavouriteRow
  mealLog: MealLog
  settings: SettingsRow
}

export type SyncKind = keyof SyncRows

/**
 * What each kind looks like on the wire, where that is not what is held. Only the workout
 * differs, and only in the one field.
 */
export interface SyncWire extends Omit<SyncRows, 'workout'> {
  workout: WorkoutRow
}

export const SYNC_KINDS: readonly SyncKind[] = [
  'set',
  'workout',
  'template',
  'version',
  'enrollment',
  'weighin',
  'metric',
  'food',
  'entry',
  'day',
  'goal',
  'favourite',
  'meal',
  'mealFavourite',
  'mealLog',
  'settings',
]

/** One row's move: what it is now, or that it is gone. */
export interface Change<K extends SyncKind = SyncKind> {
  readonly seq: number
  readonly kind: K
  readonly id: string
  readonly deleted: boolean
  /** Absent on a tombstone, and only then. */
  readonly row?: SyncWire[K]
}

export interface SyncPage {
  readonly changes: Change[]
  /** The cursor to ask from next. */
  readonly next: number
  /** Whether asking again from `next` would bring anything. */
  readonly more: boolean
}

/** The id the settings row always wears — there being one of it. */
export const SETTINGS_ID = 'me'

/**
 * The moment a write says it was made, to the millisecond — the one shape every `updatedAt`
 * wears, on the wire and in the mirror alike. Sorting these as strings sorts them by time,
 * the format being fixed-width and biggest-first, so a comparison needs no parsing.
 */
export function updatedAtOf(at: number): string {
  return new Date(at).toISOString()
}

/** Which of two of them is the later. */
export function laterOf(a: string, b: string): string {
  return a > b ? a : b
}

/** Newest first, for a list ordered by when its rows were last written. */
export function byNewestUpdate(a: { updatedAt: string }, b: { updatedAt: string }): number {
  return a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0
}
